/*
 * Copyright (c) Nexign, JSC, 2019-2021.
 */
/**
 * The types for page Bills
 *
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @version 1.14.1
 * @since 1.1.0
 */
import { ColumnFieldType, TableFooterColumnFieldType } from '../../components/Table/types/columnTypes';
import { TableFooterNameType } from '../../components/Table/types/tableTypes';
import { EBillsTypeTranslation } from '../../modules/internationalization/types/pages/bills';

interface IBillPayStatus {
    id: number;
    def: string;
}

export interface IBills {
    amount: number;
    billDate: number;
    billDebt: number;
    billId: number;
    billNumber: string;
    billPaid: number;
    checked: boolean;
    clientId: number;
    clientName: string;
    contract: string;
    customer: string;
    endDate: number;
    fucturies: string;
    id: number;
    paymentDate: number;
    payStatus: IBillPayStatus;
    startDate: number;
}

/**
 * @description Describe bills table column
 * @interface BillsField
 */
export enum BillsField {
    billNumber = 'billNumber',
    amount = 'amount',
    billDate = 'billDate',
    billDebt = 'billDebt',
    billPaid = 'billPaid',
    billPeriod = 'billPeriod',
    customer = 'customer',
    fucturies = 'fucturies',
    paidAmount = 'paidAmount',
    paymentDate = 'paymentDate',
    payStatus = 'payStatus',
    unpaidAmount = 'unpaidAmount',
}

export const billsColumnNames: BillsField[] = [
    BillsField.billNumber,
    BillsField.amount,
    BillsField.billDate,
    BillsField.billDebt,
    BillsField.billPaid,
    BillsField.billPeriod,
    BillsField.customer,
    BillsField.fucturies,
    BillsField.paidAmount,
    BillsField.paymentDate,
    BillsField.payStatus,
    BillsField.unpaidAmount,
];

export enum BillsTableFooterField {
    amount = 'amount',
    paid = 'paid',
    quantity = 'quantity',
    total = 'total',
    unpaidAmount = 'unpaidAmount',
    unpaidBillsAllowed = 'unpaidBillsAllowed',
}

export interface IBillsTableFooter {
    [BillsTableFooterField.amount]: number; // summary amount
    [BillsTableFooterField.paid]: number; // summary paidAmount
    [BillsTableFooterField.unpaidBillsAllowed]: boolean; // ???
    [BillsTableFooterField.unpaidAmount]: number; // Summary unpaidAmount
    [BillsTableFooterField.quantity]: number; // ???
}

export const MapBillsFieldToFooter: { [key in ColumnFieldType]?: TableFooterColumnFieldType } = {
    [BillsField.amount]: BillsTableFooterField.amount,
    [BillsField.billPaid]: BillsTableFooterField.paid,
    [BillsField.billDebt]: BillsTableFooterField.unpaidAmount,
};

export enum PageBillsType {
    BILLS = 'BILLS',
    BILLS_ACCOUNT = 'BILLS_ACCOUNT',
    BILLS_UNPAID = 'BILLS_UNPAID',
    BILLS_UNPAID_ACCOUNT = 'BILLS_UNPAID_ACCOUNT',
}

export const MapPageBillsTypeToTableFooterName: { [key in PageBillsType]: TableFooterNameType } = {
    [PageBillsType.BILLS]: TableFooterNameType.bills,
    [PageBillsType.BILLS_UNPAID]: TableFooterNameType.billsUnpaid,
    [PageBillsType.BILLS_ACCOUNT]: TableFooterNameType.billsAccountId,
    [PageBillsType.BILLS_UNPAID_ACCOUNT]: TableFooterNameType.billsUnpaidAccountId,
};

export const MapPageBillsTypeToBillsTypeTranslation: { [key in PageBillsType]: EBillsTypeTranslation } = {
    [PageBillsType.BILLS]: EBillsTypeTranslation.bills,
    [PageBillsType.BILLS_UNPAID]: EBillsTypeTranslation.billsUnpaid,
    [PageBillsType.BILLS_ACCOUNT]: EBillsTypeTranslation.billsAccount,
    [PageBillsType.BILLS_UNPAID_ACCOUNT]: EBillsTypeTranslation.billsUnpaidAccount,
};
